'use client';

import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import { capture } from '@/lib/analytics/posthog';
import { ACTIVE_ORDERS_QK, TRADE_HISTORY_QK } from '@/lib/api/history/hooks';
import type { TpSlOrder } from '@/lib/api/order/types';
import { useAuthContext } from '@/lib/auth/AuthProvider';
import { WS_ENDPOINTS } from '@/lib/config/websocket';
import { useWebSocket } from '@/lib/hooks/useWebSocket';
import type {
  OrderPlacementResultMessage,
  PlacementStatus,
  TpSlUpdateMessage,
} from '@/lib/types/websocket';

// Query key prefix for TP/SL orders
const TPSL_ORDERS_QK = 'tpSlOrders';

export type OrderPlacementResultHandler = (result: {
  orderId: string;
  userId: string;
  status: PlacementStatus;
  error?: string | null;
}) => void;

export function useOrderPlacementWebSocket(
  onResult?: OrderPlacementResultHandler,
  options?: {
    autoConnect?: boolean;
    showToasts?: boolean;
    invalidateQueries?: boolean;
    outcomeId?: string; // ✅ Add outcomeId for targeted invalidation
    eventId?: string; // ✅ Add eventId for targeted invalidation
    toastMessages?: {
      error?: string;
    };
  },
) {
  const { isAuthenticated } = useAuthContext();
  const queryClient = useQueryClient();
  const onResultRef = useRef<OrderPlacementResultHandler | undefined>(undefined);

  const {
    autoConnect: autoConnectOption = true,
    showToasts: showToastsOption = true,
    invalidateQueries: invalidateQueriesOption = false, // ❌ Disabled by default - invalidation is centralized in OrderForm
    outcomeId,
    eventId,
    toastMessages,
  } = options || {};

  // Keep latest handler in ref to avoid re-creating connect() on each render
  useEffect(() => {
    onResultRef.current = onResult;
  }, [onResult]);

  const handleOrderPlacementResult = useCallback(
    (msg: OrderPlacementResultMessage) => {
      const { d: data, r: error } = msg;

      if (!data) {
        console.warn('OrderPlacementResult: missing data in message', msg);
        return;
      }

      const result = {
        orderId: data.i,
        userId: data.u,
        status: data.s,
        error: error || undefined,
      };

      // Call custom handler if provided
      onResultRef.current?.(result);

      // Failures only: a placed order is announced by the wallet's balance toast.
      if (showToastsOption && result.status !== 'SUCCESS') {
        toast.error(error || toastMessages?.error || 'Order placement failed');
      }

      // Invalidate queries to refresh data
      if (invalidateQueriesOption) {
        if (outcomeId && eventId) {
          // ✅ Targeted invalidation for specific outcome only
          queryClient.invalidateQueries({
            queryKey: [TRADE_HISTORY_QK, outcomeId],
            exact: true,
          });
          queryClient.invalidateQueries({
            queryKey: [TRADE_HISTORY_QK, 'has-data', eventId, outcomeId],
            exact: true,
          });
          queryClient.invalidateQueries({
            queryKey: [ACTIVE_ORDERS_QK, outcomeId],
          });
          queryClient.invalidateQueries({
            queryKey: [ACTIVE_ORDERS_QK, 'has-data', outcomeId],
            exact: true,
          });
        } else {
          // ❌ Fallback to broad invalidation if no outcomeId provided
          queryClient.invalidateQueries({ queryKey: [TRADE_HISTORY_QK] });
          queryClient.invalidateQueries({ queryKey: [ACTIVE_ORDERS_QK] });
        }
      }
    },
    [showToastsOption, invalidateQueriesOption, queryClient, outcomeId, eventId, toastMessages],
  );

  // Handle TPSL_UPDATE messages - updates TP/SL orders cache directly
  const handleTpSlUpdate = useCallback(
    (msg: TpSlUpdateMessage) => {
      const { d: data } = msg;

      if (!data) {
        console.warn('TpSlUpdate: missing data in message', msg);
        return;
      }
      if (data.s === 'EXECUTED') capture('predict_tpsl_executed', { order_id: data.i });

      // Find all tpSlOrders queries matching the assetId from the message
      const allQueries = queryClient.getQueryCache().findAll({
        queryKey: [TPSL_ORDERS_QK],
      });

      allQueries.forEach((query) => {
        const queryKey = query.queryKey;
        // Query key format: ['tpSlOrders', assetId, status, primary]
        const queryAssetId = queryKey[1] as string | undefined;

        // Only update queries that match the assetId from the message
        if (queryAssetId !== data.a) {
          return;
        }

        queryClient.setQueryData<TpSlOrder[]>(queryKey, (oldData) => {
          if (!oldData) return oldData;

          // Handle different status updates
          if (data.s === 'CANCELLED' || data.s === 'EXECUTED' || data.s === 'REPLACED') {
            // Remove the order from the list
            const filtered = oldData.filter((order) => order.id !== data.i);
            // Only return if something was actually removed
            return filtered.length !== oldData.length ? filtered : oldData;
          }

          if (data.s === 'PARTIALLY_EXECUTED') {
            // Update the order quantity (shares were partially sold)
            const hasOrder = oldData.some((order) => order.id === data.i);
            if (!hasOrder) return oldData;

            return oldData.map((order) =>
              order.id === data.i
                ? {
                    ...order,
                    quantity: data.q,
                    takeProfitPrice: data.tpp,
                    stopLossPrice: data.slp,
                  }
                : order,
            );
          }

          if (data.s === 'ACTIVE') {
            // Check if order already exists
            const existingIndex = oldData.findIndex((order) => order.id === data.i);

            if (existingIndex >= 0) {
              // Update existing order
              return oldData.map((order) =>
                order.id === data.i
                  ? {
                      ...order,
                      quantity: data.q,
                      takeProfitPrice: data.tpp,
                      stopLossPrice: data.slp,
                    }
                  : order,
              );
            }

            // Add new order (API doesn't return order ID in POST response, it comes via WebSocket)
            const newOrder: TpSlOrder = {
              id: data.i,
              quantity: data.q,
              takeProfitPrice: data.tpp,
              stopLossPrice: data.slp,
            };
            return [newOrder, ...oldData];
          }

          return oldData;
        });
      });
    },
    [queryClient],
  );

  const orderPlacementSocket = useWebSocket({
    url: WS_ENDPOINTS.orderPlacement,
    autoConnect: autoConnectOption && isAuthenticated,
    reconnect: true,
    onOpen: () => {},
    onOrderPlacementResult: handleOrderPlacementResult,
    onTpSlUpdate: handleTpSlUpdate,
    onErrorMessage: (errorMsg) => {
      console.error('Order Placement WebSocket error:', errorMsg);
      if (showToastsOption) {
        toast.error(errorMsg.message || 'Order placement WebSocket error');
      }
    },
    onClose: (_event) => {},
  });

  // Reconnect socket when auth state changes
  useEffect(() => {
    if (isAuthenticated && autoConnectOption) {
      orderPlacementSocket.connect();
    } else {
      orderPlacementSocket.disconnect();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAuthenticated,
    autoConnectOption,
    orderPlacementSocket.disconnect,
    orderPlacementSocket.connect,
  ]);

  return {
    socket: orderPlacementSocket.socket,
    readyState: orderPlacementSocket.readyState,
    connect: orderPlacementSocket.connect,
    disconnect: orderPlacementSocket.disconnect,
  };
}
